/**
 * Card Reader Profile System Types
 * Based on the FlowMatch-based detection profile system from mobile app
 */

import { Timestamp } from 'firebase/firestore';

// Profile categories based on card reader types - simplified to 2 main categories
export type ProfileCategory = 'dip' | 'swipe';

// Detection modes for the system
export type DetectionMode = 'STANDALONE_MODE' | 'PROFILE_MODE';

// Scan data structure from sensor readings
export interface ScanData {
  timestamp: Date | Timestamp;
  sensorReadings: number[]; // Raw sensor data array
  duration: number; // Scan duration in milliseconds
  deviceId: string;
  scanId: string;
  classification?: 'clean' | 'skimmer' | 'unknown';
  metadata?: {
    temperature?: number;
    voltage?: number;
    signalStrength?: number;
  };
}

// FlowMatch threshold configuration
export interface DetectionThresholds {
  primaryThreshold: number;
  secondaryThreshold: number;
  combinationLogic: 'OR' | 'AND'; // How to combine multiple thresholds
  confidence: number; // Confidence level (0-1)
  optimized: boolean; // Whether thresholds have been optimized
  trainingAccuracy?: number; // Accuracy achieved during training
  /**
   * Mirrors mobile `ProfileMode.DetectionThresholds` when synced from the app.
   * Mobile UI labels: Average / Minimum / Maximum; logic via `useOr` (false = AND all exceed).
   */
  averageThreshold?: number;
  minimumThreshold?: number;
  maximumThreshold?: number;
  useAvg?: boolean;
  useMin?: boolean;
  useMax?: boolean;
  /** true = OR (any exceed), false = AND (all exceed) — matches mobile */
  useOr?: boolean;
}

// Reference curve for FlowMatch comparison
export interface ReferenceCurve {
  id: string;
  signature: number[]; // Normalized sensor signature
  originalScanId: string;
  diversity: number; // Diversity score used for selection
  weight: number; // Weight in detection algorithm
  metadata?: {
    collectionDate: Date | Timestamp;
    deviceId: string;
    notes?: string;
  };
}

/**
 * Firestore sometimes returns dense numeric arrays as plain objects `{ "0": n, "1": n, ... }`.
 * Without this, charts may skip curves or show fewer lines than `referenceCurves.length`.
 */
export function normalizeSignatureData(raw: unknown): number[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => /^\d+$/.test(k));
    if (keys.length === 0) return [];
    return keys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => Number(o[k]))
      .filter((v) => Number.isFinite(v));
  }
  return [];
}

// Training session for profile creation
export interface TrainingSession {
  id: string;
  profileId: string;
  profileName: string;
  category: ProfileCategory;
  startTime: Date | Timestamp;
  endTime?: Date | Timestamp;
  status: 'in_progress' | 'completed' | 'failed' | 'cancelled';
  targetScans: number; // Target number of scans (usually 100)
  collectedScans: number; // Current number of scans collected
  scans: ScanData[]; // Collected scan data
  processingProgress: number; // 0-100, processing completion percentage
  createdBy: string; // User ID who created the session
  notes?: string;
}

// Performance metrics for profile evaluation
export interface PerformanceMetrics {
  accuracy: number; // Overall accuracy (0-1)
  precision: number; // Precision (0-1)
  recall: number; // Recall/Sensitivity (0-1)
  f1Score: number; // F1 Score (0-1)
  specificity: number; // Specificity (0-1)
  confusionMatrix: {
    truePositives: number;
    falsePositives: number;
    trueNegatives: number;
    falseNegatives: number;
  };
  optimizationResults?: {
    originalThresholds: DetectionThresholds;
    optimizedThresholds: DetectionThresholds;
    improvementPercent: number;
  };
}

// Profile usage statistics
export interface ProfileStats {
  totalScansProcessed: number;
  cleanDetections: number;
  skimmerDetections: number;
  averageConfidence: number;
  lastUsed: Date | Timestamp;
  usageCount: number;
  falsePositiveRate: number;
  falseNegativeRate: number;
  effectivenessScore: number; // Overall effectiveness rating (0-100)
}

// Main detection profile structure
export interface DetectionProfile {
  id: string;
  name: string;
  description?: string;
  category: ProfileCategory;
  version: number; // For versioning and updates
  readerImageUrl?: string; // Thumbnail image URL like mobile app
  /** Present on mobile-synced profiles — used for display when no photo URL */
  trainingInfo?: {
    scanCount?: number;
    readerModel?: string;
    location?: string;
    sessionId?: string;
  };
  knownSkimmers?: {
    overlay: string[];
    deepInsert: string[];
  };
  
  // Core FlowMatch components
  referenceCurves: ReferenceCurve[]; // Usually 20 curves
  thresholds: DetectionThresholds;
  
  // Training and validation data
  trainingSessionId: string;
  validationMetrics?: PerformanceMetrics;
  
  // Profile metadata
  createdBy: string; // User ID
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  masterOwnerID: string; // Company/organization ID
  
  // Status and configuration
  isActive: boolean; // Whether this profile is currently active for its category
  isShared: boolean; // Whether profile can be shared with team
  tags: string[]; // For categorization and search
  
  // Usage and performance tracking
  stats: ProfileStats;
  
  // Technical parameters
  processingParameters: {
    signatureLength: number; // Length of processed signature
    samplingRate: number; // Data sampling rate
    filterParameters: {
      lowPassCutoff?: number;
      highPassCutoff?: number;
      smoothingWindow?: number;
    };
    normalizationMethod: 'z-score' | 'min-max' | 'robust';
    activeCroppingEnabled: boolean;
  };
}

// Profile index for efficient listing
export interface ProfileIndex {
  profiles: {
    id: string;
    name: string;
    category: ProfileCategory;
    isActive: boolean;
    lastUsed: Date | Timestamp;
    effectivenessScore: number;
  }[];
  activeProfilesByCategory: Record<ProfileCategory, string | null>;
  lastSync: Date | Timestamp;
  totalProfiles: number;
}

// Training result data structure
export interface TrainingResults {
  profileId: string;
  sessionId: string;
  success: boolean;
  metrics: PerformanceMetrics;
  generatedProfile?: DetectionProfile;
  processingLog: string[]; // Log messages from processing
  duration: number; // Training duration in milliseconds
  recommendedThresholds: DetectionThresholds;
  qualityScore: number; // Overall training quality (0-100)
}

// Profile comparison data
export interface ProfileComparison {
  profile1: DetectionProfile;
  profile2: DetectionProfile;
  similarities: {
    curveCorrelation: number; // Average correlation between reference curves
    thresholdSimilarity: number; // Similarity of thresholds
    performanceSimilarity: number; // Similarity of performance metrics
  };
  differences: {
    effectivenessDelta: number;
    accuracyDelta: number;
    usageDelta: number;
  };
  recommendation: 'prefer_first' | 'prefer_second' | 'equivalent' | 'combine';
}

// Profile export/import formats
export interface ProfileExportData {
  profile: DetectionProfile;
  trainingSession?: TrainingSession;
  exportMetadata: {
    exportedAt: Date | Timestamp;
    exportedBy: string;
    version: string;
    format: 'json' | 'csv' | 'binary';
  };
}

// Firebase collection names for profile system
export const PROFILE_COLLECTIONS = {
  DETECTION_PROFILES: 'detectionProfiles',
  TRAINING_SESSIONS: 'trainingSessions',
  PROFILE_INDEXES: 'profileIndexes',
  SCAN_DATA: 'scanData',
} as const;

// Profile category metadata
export const PROFILE_CATEGORY_INFO: Record<ProfileCategory, {
  displayName: string;
  description: string;
  icon: string;
  color: string;
}> = {
  dip: {
    displayName: 'DIP Reader',
    description: 'Card readers with chip insertion (DIP)',
    icon: '🏧',
    color: '#4A90E2'
  },
  swipe: {
    displayName: 'Swipe Reader',
    description: 'Magnetic stripe card readers (swipe/POS)',
    icon: '💳',
    color: '#7ED321'
  }
} as const;

// Type guards and validation functions
export const isValidProfileCategory = (category: string): category is ProfileCategory => {
  return ['dip', 'swipe'].includes(category);
};

export const isValidDetectionProfile = (data: any): data is DetectionProfile => {
  return (
    data &&
    typeof data.id === 'string' &&
    typeof data.name === 'string' &&
    isValidProfileCategory(data.category) &&
    Array.isArray(data.referenceCurves) &&
    data.thresholds &&
    typeof data.thresholds.primaryThreshold === 'number' &&
    typeof data.thresholds.secondaryThreshold === 'number'
  );
};

export const isValidScanData = (data: any): data is ScanData => {
  return (
    data &&
    data.timestamp &&
    Array.isArray(data.sensorReadings) &&
    data.sensorReadings.length > 0 &&
    typeof data.duration === 'number' &&
    typeof data.deviceId === 'string' &&
    typeof data.scanId === 'string'
  );
};

// Helper functions for profile management
export const calculateProfileEffectiveness = (stats: ProfileStats): number => {
  const accuracy = (stats.cleanDetections + stats.totalScansProcessed - stats.skimmerDetections) / 
                   Math.max(stats.totalScansProcessed, 1);
  const usageWeight = Math.min(stats.usageCount / 100, 1); // Normalize usage count
  const confidenceWeight = stats.averageConfidence;
  
  return Math.round((accuracy * 0.5 + usageWeight * 0.3 + confidenceWeight * 0.2) * 100);
};

export const getProfileCategoryColor = (category: ProfileCategory | string): string => {
  // Handle old categories by mapping them to new ones
  const categoryMap: Record<string, ProfileCategory> = {
    'metal_bezel_dip': 'dip',
    'plastic_bezel_dip': 'dip', 
    'pos': 'swipe'
  };
  
  const mappedCategory = categoryMap[category as string] || category as ProfileCategory;
  return PROFILE_CATEGORY_INFO[mappedCategory]?.color || '#4A90E2'; // Default to blue
};

export const getProfileCategoryIcon = (category: ProfileCategory | string): string => {
  // Handle old categories by mapping them to new ones
  const categoryMap: Record<string, ProfileCategory> = {
    'metal_bezel_dip': 'dip',
    'plastic_bezel_dip': 'dip',
    'pos': 'swipe'
  };
  
  const mappedCategory = categoryMap[category as string] || category as ProfileCategory;
  return PROFILE_CATEGORY_INFO[mappedCategory]?.icon || '🏧'; // Default to ATM icon
};

export const getProfileCategoryDisplayName = (category: ProfileCategory | string): string => {
  // Handle old categories by mapping them to new ones
  const categoryMap: Record<string, ProfileCategory> = {
    'metal_bezel_dip': 'dip',
    'plastic_bezel_dip': 'dip',
    'pos': 'swipe'
  };
  
  const mappedCategory = categoryMap[category as string] || category as ProfileCategory;
  return PROFILE_CATEGORY_INFO[mappedCategory]?.displayName || 'DIP Reader'; // Default to DIP Reader
};
